import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { To, generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { DEFAULT_SEASON_SLUG } from '@sorare/core/src/constants/routes';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { ManagerDivision } from 'components/rivals/ManagerDivision';

import { Row } from './Row';
import { LeaderboardOverview_footballRivalsManager } from './__generated__/index.graphql';

const Content = styled(Vertical)`
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-400);
  background-color: var(--c-black);
  overflow: hidden;
`;
const EmptyState = styled(Horizontal).attrs({ center: true })`
  text-align: center;
  height: 150px;
  padding: 0 var(--triple-unit);
`;

type Props = {
  manager: LeaderboardOverview_footballRivalsManager;
  customTitle?: ReactNode;
  to?: To;
};
export const LeaderboardOverview = ({ manager, customTitle, to }: Props) => {
  const { formatMessage } = useIntl();
  const { currentLeaderboard, user } = manager || {};

  const { rankings = [], leaderboardConfig } = currentLeaderboard || {};
  const managerRanking = manager.currentRanking;

  const managerRankIndex = rankings.findIndex(r => r.id === managerRanking?.id);

  const promotion = managerRanking?.divisionChange === DivisionChange.PROMOTED;
  const relegation =
    managerRanking?.divisionChange === DivisionChange.RELEGATED;
  const aroundMyRank = rankings.slice(
    Math.max(managerRankIndex - 1, 0),
    Math.min(managerRankIndex + 2, rankings.length)
  );

  return (
    <LinkBox>
      <Box
        labelProps={{
          ...(promotion
            ? {
                bgColor: 'var(--c-rivals-friendlies)',
                color: 'var(--c-black)',
                value: formatMessage(fantasy.promotion),
              }
            : {}),
          ...(relegation
            ? {
                bgColor: 'var(--c-red-600)',
                color: 'var(--c-white)',
                value: formatMessage(fantasy.relegation),
              }
            : {}),
        }}
      >
        <Vertical gap={2}>
          <LinkOther
            as={Link}
            to={generatePath(
              FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG,
              {
                seasonSlug: DEFAULT_SEASON_SLUG,
                userSlug: user?.slug,
              }
            )}
          >
            <ManagerDivision
              customTitle={customTitle}
              divisionConfig={manager.divisionConfig}
              ranking={managerRanking}
            />
          </LinkOther>
          <LinkOverlay as={to ? Link : 'div'} to={to}>
            <Content gap={0}>
              {currentLeaderboard && leaderboardConfig ? (
                aroundMyRank.map(ranking => (
                  <Row
                    key={ranking.id}
                    ranking={ranking}
                    highlight={managerRanking?.id === ranking.id}
                  />
                ))
              ) : (
                <EmptyState>
                  <LabelM>
                    <FormattedMessage
                      id="LeaderboardOverview.emptyState"
                      defaultMessage="Not entered this week."
                    />
                  </LabelM>
                </EmptyState>
              )}
            </Content>
          </LinkOverlay>
        </Vertical>
      </Box>
    </LinkBox>
  );
};

LeaderboardOverview.fragments = {
  footballRivalsManager: gql`
    fragment LeaderboardOverview_footballRivalsManager on FootballRivalsPublicManagerInterface {
      user {
        slug
      }
      currentLeaderboard {
        id
        leaderboardConfig {
          promotedCount
          relegatedCount
        }
        rankings {
          id
          ...Row_footballRivalsDivisionRanking
        }
      }
      currentRanking {
        id
        divisionChange
        ...ManagerDivision_footballRivalsDivisionRanking
      }
      divisionConfig {
        id
        ...ManagerDivision_footballRivalsDivisionLeaderboardDivisionConfig
      }
    }
    ${ManagerDivision.fragments.footballRivalsDivisionRanking}
    ${ManagerDivision.fragments.footballRivalsDivisionLeaderboardDivisionConfig}
    ${Row.fragments.footballRivalsDivisionRanking}
  ` as TypedDocumentNode<LeaderboardOverview_footballRivalsManager>,
};
